<template>
  <content-modal
    ref="embedWebsiteModal"
    class="embed-website-modal embed-modal"
    :visible="visible"
    :close-aria-label="closeAriaLabel"
    :size="size"
    :auto-hide-off="autoHideOff"
    @modal-shown="actionShown"
    @modal-hidden="actionHidden"
    @modal-hide-request="actionHideRequest"
  >
    <template #title>{{ t("tinymce_plugin_embed_website_modal_title") }}</template>
    <template #content>
      <modal-content>
        <template #content>
          <overlay-loader v-if="isLoading" />
          <section v-else>
            <cv-form class="embed-web">
              <dl class="field-set type-button choose-approved-website">
                <dt class="field-label">
                  <div>{{ t("tinymce_plugin_embed_website_modal_message_label") }}</div>
                  <cv-tooltip
                    direction="bottom"
                    :alignment="websiteTooltipAlignment"
                    :tip="t('tinymce_plugin_embed_website_unapprove_subtitle_label')"
                  >
                    <svg-icon name="Info24" kind="action" />
                  </cv-tooltip>
                </dt>
                <dd class="field-input">
                  <cv-button
                    class="btn-browse"
                    type="button"
                    kind="tertiary"
                    size="small"
                    @click="handleBrowse"
                  >
                    <svg-icon name="Embed24" kind="action" />
                    {{ t("tinymce_plugin_embed_website_browse_websites_label") }}
                  </cv-button>
                </dd>
              </dl>

              <dl class="field-set type-radio">
                <dd class="field-input">
                  <cv-radio-group vertical="vertical" legend-text="">
                    <input-radio
                      id="embed-url"
                      kind="white"
                      value="embed-url"
                      :selected-value="selectedEmbedOption"
                      @change="(data: unknown) => handleEmbedWebOption(data as EmbedOption)"
                    >
                      {{ t("tinymce_plugin_embed_website_embed_url_label") }}
                    </input-radio>
                    <input-radio
                      id="embed-code"
                      kind="white"
                      value="embed-code"
                      :selected-value="selectedEmbedOption"
                      :checked="isEmbedCodeSelected"
                      @change="(data: unknown) => handleEmbedWebOption(data as EmbedOption)"
                    >
                      {{ t("tinymce_plugin_embed_website_embed_code_label") }}
                    </input-radio>
                  </cv-radio-group>
                </dd>
              </dl>

              <dl class="field-set type-text">
                <dd class="field-input">
                  <text-area
                    ref="embedUrlCodeInput"
                    :error-message="error.embedCodeMessage"
                    :placeholder="embedUrlCodePlaceholder"
                    :character-limit="embedUrlCodeCharacterLimit"
                    :value="embedUrlCode"
                    :text-id="id"
                    @change="handleEmbedUrlCodeInput"
                    @input="handleClearEmbedErrorMessage"
                  />
                </dd>
              </dl>

              <div v-if="isEmbedUrlSelected" class="field-group">
                <dl class="field-set type-text">
                  <dt class="field-label">
                    {{ t("tinymce_plugin_embed_website_embed_url_height_label") }}
                  </dt>
                  <dd class="field-input">
                    <cv-number-input
                      id="url-height"
                      :model-value="urlHeight"
                      min="1"
                      step="1"
                      @keydown.enter.prevent
                      @change="handleUrlHeightChange"
                      @input="handleUrlHeightInput"
                    />
                  </dd>
                </dl>
                <dl class="field-set type-text">
                  <dt class="field-label">
                    {{ t("tinymce_plugin_embed_website_embed_url_width_label") }}
                  </dt>
                  <dd class="field-input">
                    <cv-number-input
                      id="url-width"
                      :model-value="urlWidth"
                      min="1"
                      step="1"
                      @keydown.enter.prevent
                      @change="handleUrlWidthChange"
                      @input="handleUrlWidthInput"
                    />
                  </dd>
                </dl>
              </div>

              <dl v-if="showWebsiteInfo" class="field-set info-wrapper">
                <dd v-if="!isWebsiteLoading" class="field-value">
                  <div class="image-placeholder">
                    <EmptyError v-if="websiteIconEmpty" class="image" />
                    <img v-else :src="getIconUrl()" alt="" />
                  </div>
                  <div class="info">
                    <h5 class="website-title">
                      <span>{{ websiteInfo?.name }}</span>
                    </h5>
                    <div class="website-description">
                      <rich-text :content="websiteInfo?.description" :expanded="false" />
                    </div>
                  </div>
                </dd>
                <div v-else class="info-loading">
                  <div class="image-loading-state"></div>
                  <cv-skeleton-text paragraph="paragraph" width="100%" />
                </div>
              </dl>
            </cv-form>
          </section>
        </template>
        <template #tools>
          <cv-button class="cancel" kind="secondary" size="field" @click="actionHideRequest">
            {{ t("button_cancel") }}
          </cv-button>
          <fetch-data-button
            kind="primary"
            size="field"
            :loading="processingState.isLoading"
            :error="processingState.error"
            :error-message="processingState.errorMessage"
            :tip-position="processingState.tipPosition"
            @click="handleSave"
          >
            <span v-if="processingState.error">{{ t("button_state_error") }}</span>
            <span v-else>{{ t("button_save") }}</span>
          </fetch-data-button>
        </template>
      </modal-content>
    </template>
  </content-modal>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance, nextTick, onMounted, ref } from "vue";
import i18n from "@plugins/i18n.ts";
import embedUtils from "../../utils/embed.utils";
import randomUtils from "@utils/random.utils";
import DeviceUtils from "@utils/device.utils";
import whitelistWebsiteApi from "@/apis/whitelist.website.api";
import { isEmpty, isNull } from "@utils/var.check";
import EmptyError from "@images/placeholder/empty-states/EmptyImage.svg?component";
import modalEventBus from "@plugins/modal/service/event-bus";
import embedComponentEventBus from "@plugins/richtext/plugins/embed-website/service/event-bus";
import mediaEventBus from "@plugins/media/service/event-bus";
import EventBusUtils from "@plugins/common/service/event.bus.utils";
import ContentModal from "@plugins/modal/components/ContentModal.vue";
import ModalContent from "@plugins/modal/components/ModalContent.vue";
import SvgIcon from "@plugins/common/components/SvgIcon.vue";
import InputRadio from "@plugins/common/components/InputRadio.vue";
import TextArea from "@plugins/common/components/TextArea.vue";
import RichText from "@plugins/richtext/components/RichText.vue";
import FetchDataButton from "@plugins/common/components/FetchDataButton.vue";
import { useI18n } from "vue-i18n";
import { useDirty } from "@plugins/common/composables/page-dirty.ts";
import { EmbedWebsiteParams, PresetData, WebsiteInfo } from "@plugins/richtext/types.ts";
import { EMBED_OPTION, EmbedOption } from "@plugins/richtext/richtext.constant.ts";

const props = defineProps<{ params: EmbedWebsiteParams }>();
const emit = defineEmits<
  (
    e: "onHandleBrowseClick",
    payload: {
      embedType: EmbedOption;
      urlCode: string;
      height: number | string;
      width: number | string;
    },
  ) => void
>();

const { t } = useI18n();
const instance = getCurrentInstance();

const closeAriaLabel = "Close";
const size = "large";
const autoHideOff = true;

const visible = ref(true);
const id = randomUtils.randomId();
const dirty = useDirty(ref(id));

const selectedEmbedOption = ref<EmbedOption>(EMBED_OPTION.EMBED_URL);
const embedUrlCode = ref("");
const embedWebsiteUuid = ref(""); // domain
const allowedNode = props.params.allowedNode;

const error = ref({
  urlMessage: "",
  embedCodeMessage: "",
  heightMessage: "",
  widthMessage: "",
});

const processingState = ref({
  isLoading: false,
  error: false,
  errorMessage: "",
  tipPosition: "top",
});

const allowedDomainUrls = ref<Record<string, string>>(props.params.allowedDomainUrls || {});
const urlHeight = ref<number | string>("480");
const urlWidth = ref<number | string>("680");
const websiteInfo = ref<WebsiteInfo | null>(null);
const isDomainUnapproved = ref(false);
const isLoading = ref(true);
const isWebsiteLoading = ref(false);
const isEdit = ref(false);

const embedUrlCodeInput = ref<InstanceType<typeof TextArea>>();

const isEmbedUrlSelected = computed(() => selectedEmbedOption.value === EMBED_OPTION.EMBED_URL);
const isEmbedCodeSelected = computed(() => selectedEmbedOption.value === EMBED_OPTION.EMBED_CODE);

const embedUrlCodePlaceholder = computed(() =>
  isEmbedCodeSelected.value
    ? i18n.t("embed_setting_embed_code_placeholder")
    : i18n.t("embed_setting_embed_url_placeholder"),
);

const embedUrlCodeCharacterLimit = computed(() => (isEmbedCodeSelected.value ? 2000 : 500));

const showWebsiteInfo = computed(
  () => !isDomainUnapproved.value && (!isEmpty(websiteInfo.value) || isWebsiteLoading.value),
);
const websiteIconEmpty = computed(() => websiteInfo.value && isEmpty(websiteInfo.value.iconUrl));
const websiteTooltipAlignment = computed(() => (DeviceUtils.isMobile() ? "end" : "center"));

onMounted(() => {
  if (!isNull(props.params.presetData)) {
    // If a website has been selected from browse website, populate the data in the fields
    nextTick(() => setFieldValues());
  } else if (props.params.embedConfig) {
    isEdit.value = true;
    const embedData = props.params.embedConfig;
    embedWebsiteUuid.value = embedData.frameAttributes.websiteuuid ?? "";
    selectedEmbedOption.value = embedData.type;
    getWebsiteInfo(embedData.frameAttributes.websiteuuid ?? "");

    if (embedData.type === EMBED_OPTION.EMBED_URL) {
      embedUrlCode.value = String(embedData.frameAttributes.src);
      urlWidth.value = embedData.frameAttributes.width;
      urlHeight.value = embedData.frameAttributes.height;
    } else {
      delete embedData.frameAttributes.websiteuuid;
      delete embedData.frameAttributes["data-urlappendix"];
      const cloneFrame = embedUtils.getSanitizedSlsFrame(embedData.frameAttributes);
      embedUrlCode.value = embedUtils.convertSlsFrameToIframe(cloneFrame).outerHTML;
    }
  }

  isLoading.value = false;
});

function isProcessing() {
  return processingState.value.isLoading;
}
function setProcessing() {
  processingState.value.isLoading = true;
}
function setProcessingError() {
  processingState.value.isLoading = false;
  processingState.value.error = true;
  // short delay before removing the error state.
  setTimeout(() => {
    resetProcessingState();
  }, 3000);
}
function resetProcessingState() {
  processingState.value.isLoading = false;
  processingState.value.error = false;
}

function handleSave() {
  if (isProcessing()) {
    return;
  }
  setProcessing();
  resetError();

  let isValidNumber = true;
  if (!isEmbedCodeSelected.value) {
    // numeric checks come from the Carbon number-input; we guard here too
    if (isNaN(Number(urlHeight.value))) {
      setProcessingError();
      isValidNumber = false;
    }
    if (isNaN(Number(urlWidth.value))) {
      setProcessingError();
      isValidNumber = false;
    }
  }

  if (validateCharacterLimit() && validateUrlCode() && isValidNumber) {
    let embedData:
      | { data: { urlCode: string; unapproved: true } }
      | {
          type: EmbedOption;
          data: {
            urlCode: string;
            websiteUuid: string;
            urlAppendix?: string;
            width?: number | string;
            height?: number | string;
          };
        };

    if (isDomainUnapproved.value) {
      embedData = {
        data: {
          urlCode: embedUrlCode.value,
          unapproved: true,
        },
      };
    } else {
      embedData = {
        type: selectedEmbedOption.value,
        data: {
          urlCode: embedUrlCode.value,
          websiteUuid: embedWebsiteUuid.value,
        },
      };
      const appendix = getUrlAppendix(selectedEmbedOption.value, embedUrlCode.value);
      if (!isEmpty(appendix)) {
        embedData.data.urlAppendix = appendix;
      }
      if (isEmbedUrlSelected.value) {
        embedData.data.width = urlWidth.value;
        embedData.data.height = urlHeight.value;
      }
    }

    dirty.removeDirty(id);
    resetProcessingState();

    if (isEdit.value) {
      mediaEventBus.$emit("settingsUpdated", getSettingsData(embedData));
    } else {
      embedComponentEventBus.$emit("embedItemAdded", embedData);
    }
    visible.value = false;
    EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
    //restore any previous scrollability
    document.body.classList.remove("bx--body--with-modal-open");
    modalEventBus.$emit("modal-hidden", instance?.vnode.key);
  } else {
    setProcessingError();
  }
}

function getUrlAppendix(embedType: EmbedOption, urlCode: string) {
  let appendix: string;
  if (embedType === EMBED_OPTION.EMBED_CODE) {
    const iframes = embedUtils.getIframes(urlCode);
    const url = iframes[0].src;
    appendix = url.slice(allowedDomainUrls.value[embedWebsiteUuid.value].length);
  } else {
    appendix = embedUrlCode.value.slice(allowedDomainUrls.value[embedWebsiteUuid.value].length);
  }

  if (appendix === "/") return "";
  return appendix;
}

function getSettingsData(embedData) {
  let settings:
    | {
        unapproved: true;
        unapprovedUrlCallback?: ((url: string) => void) | undefined;
        data: { urlCode: string };
      }
    | {
        slsFrame: HTMLElement;
        type: EmbedOption;
        frameAttributes: Record<string, string>;
        updateCallback?: ((data: unknown) => void) | undefined;
      };

  if ("unapproved" in embedData.data) {
    settings = {
      unapproved: true,
      unapprovedUrlCallback: props.params.unapprovedUrlCallback,
      data: { urlCode: embedUrlCode.value },
    };
    return settings;
  }

  if (isEmbedCodeSelected.value) {
    const iframes = embedUtils.getIframes(embedUrlCode.value);
    const slsFrame = embedUtils.getEmbedCodeFrame(iframes[0], embedData.data, allowedNode);
    const frameAttributes = embedUtils.getValidFrameAttributes(slsFrame.attributes);
    embedUtils.updateStyleAttribute(slsFrame);
    settings = {
      slsFrame,
      type: embedData.type,
      frameAttributes,
    };
  } else {
    const slsFrame = embedUtils.getEmbedUrlFrame(embedData.data);
    settings = {
      slsFrame,
      type: embedData?.type,
      frameAttributes: embedUtils.getValidFrameAttributes(slsFrame.attributes),
    };
  }
  settings.updateCallback = props.params.editorSaveCallback;
  return settings;
}

function actionShown() {
  visible.value = true;
}
function actionHidden() {
  visible.value = false;
  EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
  modalEventBus.$emit("modal-hidden", instance?.vnode.key);
}

async function actionHideRequest(event: Record<string, string>) {
  // to prevent close modal upon clicking anywhere outside the modal container
  if (event["cv:reason"] !== "external-click") {
    const ok = await dirty.promptComponentDirty();
    if (!ok) return;
    visible.value = false;
    EventBusUtils.unWatchInstance(props.params.eventBusWatchInstances);
    //restore any previous scrollability
    document.body.classList.remove("bx--body--with-modal-open");
    modalEventBus.$emit("modal-hidden", instance?.vnode.key);
  }
}

function handleEmbedWebOption(data: EmbedOption) {
  selectedEmbedOption.value = data;
  handleEmbedUrlCodeInput(embedUrlCode.value);
}

function handleEmbedUrlCodeInput(value: string) {
  // reset
  isDomainUnapproved.value = false;
  websiteInfo.value = null;
  error.value.urlMessage = "";
  embedUrlCode.value = value.trim();
  dirty.setDirty(id);

  if (isEmpty(embedUrlCode.value) || !validateCharacterLimit() || !validateUrlCode()) {
    return;
  }

  const errorCode = validateAllowedUrl(embedUrlCode.value);
  if (errorCode === "") {
    if (isEmbedUrlSelected.value) {
      embedWebsiteUuid.value = getWebsiteUuidByDomainValue(embedUrlCode.value);
      getWebsiteInfo(embedWebsiteUuid.value);
    } else {
      const iframes = embedUtils.getIframes(embedUrlCode.value);
      embedWebsiteUuid.value = getWebsiteUuidByDomainValue(iframes[0].src);
      getWebsiteInfo(embedWebsiteUuid.value);
    }
  } else {
    isDomainUnapproved.value = true;
  }
}

function handleClearEmbedErrorMessage() {
  error.value.embedCodeMessage = "";
}

function handleUrlHeightInput(evt: { target: { valueAsNumber: number } }) {
  error.value.heightMessage = "";
  validateNumberInputFields(evt.target as HTMLInputElement);
}

function handleUrlWidthInput(evt: { target: { valueAsNumber: number } }) {
  error.value.widthMessage = "";
  validateNumberInputFields(evt.target as HTMLInputElement);
}

function validateNumberInputFields(target: HTMLInputElement) {
  const num = target.valueAsNumber;
  if (isNaN(num) || num <= 0) {
    target.value = "";
    return false;
  }
  return true;
}

function getWebsiteUuidByDomainValue(value: string) {
  const lower = value.toLowerCase();
  let uuid: string | undefined;
  let charCount = 0; // to track highest

  Object.keys(allowedDomainUrls.value).forEach((key) => {
    const domainUrl = allowedDomainUrls.value[key].toLowerCase();

    if (lower.startsWith(domainUrl) && lower.includes(domainUrl) && domainUrl.length > charCount) {
      charCount = domainUrl.length;
      uuid = key;
    }
  });

  return uuid!;
}

function validateUrlCode() {
  return isEmbedCodeSelected.value ? validateEmbedCode() : validateEmbedUrl();
}

function validateCharacterLimit() {
  return embedUrlCodeInput.value?.validateMaxLimit?.();
}

function handleUrlHeightChange(evt: { target: { valueAsNumber: number } }) {
  if (!validateNumberInputFields(evt.target as HTMLInputElement)) {
    return;
  }
  dirty.setDirty(id);
  urlHeight.value = evt.target.valueAsNumber;
}
function handleUrlWidthChange(evt: { target: { valueAsNumber: number } }) {
  if (!validateNumberInputFields(evt.target as HTMLInputElement)) {
    return;
  }
  dirty.setDirty(id);
  urlWidth.value = evt.target.valueAsNumber;
}
function getWebsiteInfo(domainUuid: string) {
  websiteInfo.value = null;
  isWebsiteLoading.value = true;
  whitelistWebsiteApi.fetchWhitelistWebsiteInfo(domainUuid).then(
    (response) => {
      if (response.code === "ok") {
        websiteInfo.value = response.data;
      }
      isWebsiteLoading.value = false;
    },
    () => {
      // keep original behaviour for errors via mixins (no-op here)
      isWebsiteLoading.value = false;
    },
  );
}

function handleBrowse() {
  // Hide this modal and open browse website subpage
  actionHidden();
  emit("onHandleBrowseClick", {
    embedType: selectedEmbedOption.value,
    urlCode: embedUrlCode.value,
    height: urlHeight.value,
    width: urlWidth.value,
  });
}

function validateEmbedUrl() {
  const errorCode = embedUtils.validateEmbedUrl(embedUrlCode.value);
  if (errorCode === "") {
    error.value.embedCodeMessage = "";
    return true;
  } else {
    error.value.embedCodeMessage = i18n.t(errorCode);
    setProcessingError();
    return false;
  }
}

function validateEmbedCode() {
  const errorCode = embedUtils.validateEmbedCode(embedUrlCode.value);
  if (errorCode === "") {
    error.value.embedCodeMessage = "";
    return true;
  } else {
    error.value.embedCodeMessage = i18n.t(errorCode);
    setProcessingError();
    return false;
  }
}

function validateAllowedUrl(code: string) {
  if (isEmbedCodeSelected.value) {
    const iframes = embedUtils.getIframes(code);
    return embedUtils.validateAllowedUrl(iframes[0].src, Object.values(allowedDomainUrls.value));
  }
  return embedUtils.validateAllowedUrl(code, Object.values(allowedDomainUrls.value));
}

function resetError() {
  error.value.embedCodeMessage = "";
  error.value.urlMessage = "";
}

function setFieldValues() {
  const presetData = props.params.presetData as PresetData;
  if (
    presetData.embedType === EMBED_OPTION.EMBED_CODE &&
    embedUtils.getIframes(presetData.urlCode).length === 0
  ) {
    presetData.urlCode = embedUtils.createIframe(presetData.urlCode).outerHTML;
  }
  urlWidth.value = presetData.width;
  urlHeight.value = presetData.height;
  selectedEmbedOption.value = presetData.embedType;
  handleEmbedUrlCodeInput(presetData.urlCode);
}

function getIconUrl() {
  return `${websiteInfo.value!.iconUrl}?t=${Date.now()}`;
}
</script>
